import * as _ from 'lodash';
import { getCategoryDisplayName, Category } from '../../../../../shared/issuesV2';
import type {
  LicenseIssueSettings,
  SecurityIssueSettings,
  LicenseIssueSettingFields,
  SecurityIssueSettingFields,
  QualityIssueSettings,
  QualityIssueSettingFields,
} from '../../../../../shared/settings/project/issues';
import type { SBOMSettingsFields } from '../../../../../shared/settings/project/sbom';

//  Common endpoint for all of the GET, PUT and PATCH requests
//  Note that this also supports the container endpoints, despite not technically being a Category.
export const getEndpoint = (orgId: number, category: Category | 'container'): `/api/organizations/${number}/settings/projects/${string}` =>
  `/api/organizations/${orgId}/settings/projects/issues/${category === 'container' ? category : getCategoryDisplayName(category)}`;

//  Generic names + types for each of the different category settings.
//  This is to help us map the "generic" settings names to the ones
//  actually used by each of the categories.
export type GenericIssueSettings = {
  isScanningEnabled: boolean;
  policyId: number | null;
  isCICheckEnabled: boolean;
  filterId: number | null;
};

//  Note that these settings are called different things depending
//  on which category is selected. This is a standard interface
//  to be used for all categories.
export type PropagateIssueCheckboxes = {
  isScanningEnabled: boolean;
  shouldUseSelectedPolicy: boolean;
  isCICheckEnabled: boolean;
  shouldUseSelectedFilter: boolean;
};

export type AnyIssueSettings = LicenseIssueSettings | SecurityIssueSettings | QualityIssueSettings;

type GenericIssueSettingsKeys = keyof GenericIssueSettings;

export type GenericIssueSettingsFields =
  | LicenseIssueSettingFields
  | SecurityIssueSettingFields
  | QualityIssueSettingFields
  | SBOMSettingsFields;

export const settingsCategoryMap: Record<Category, Record<GenericIssueSettingsKeys, GenericIssueSettingsFields>> = {
  [Category.Licensing]: {
    isScanningEnabled: 'projectDefaultLicensingIssueScanningEnabled',
    policyId: 'defaultPolicyId',
    isCICheckEnabled: 'projectDefaultLicensingStatusCheckEnabled',
    filterId: 'projectDefaultStatusCheckFilterLicensing',
  },
  [Category.Vulnerability]: {
    isScanningEnabled: 'projectDefaultSecurityIssueScanningEnabled',
    policyId: 'defaultSecurityPolicyId',
    isCICheckEnabled: 'projectDefaultSecurityStatusCheckEnabled',
    filterId: 'projectDefaultStatusCheckFilterVulnerability',
  },
  [Category.Quality]: {
    isScanningEnabled: 'projectDefaultQualityIssueScanningEnabled',
    policyId: 'defaultQualityPolicyId',
    isCICheckEnabled: 'projectDefaultQualityStatusCheckEnabled',
    filterId: 'projectDefaultStatusCheckFilterQuality',
  },
};

/**
 * Takes the category + the fetched settings and returns
 * the mapped generic issue settings.
 *
 * @example
 * const licenseSettings = {
 *   projectDefaultLicensingIssueScanningEnabled: true,
 *   defaultPolicyId: 1,
 *   projectDefaultLicensingStatusCheckEnabled: true,
 *   projectDefaultStatusCheckFilterLicensing: 1,
 * };
 *
 * const genericSettings = convertSettingsToGeneric(Category.Licensing, licenseSettings);
 * //  {
 * //    isScanningEnabled: true,
 * //    policyId: 1,
 * //    isCICheckEnabled: true,
 * //    filterId: 1,
 * //  }
 */
export const convertSettingsToGeneric = (
  category: Category,
  settings: AnyIssueSettings | null,
): GenericIssueSettings | null => {
  if (!settings) {
    return null;
  }

  const categorySpecificKeys = settingsCategoryMap[category];

  return Object.fromEntries(
    Object.entries(categorySpecificKeys)
      .map(([genericKey, categoryKey]) => [genericKey, settings[categoryKey as GenericIssueSettingsKeys]]),
  ) as GenericIssueSettings;
};

/**
 * Takes a category + the saved generic settings and returns
 * the a slice of mapped category settings. Useful for updating one field in the settings.
 *
 * @example
 * const licenseSettings = {
 *  isCICheckEnabled: true,
 * };
 *
 * const genericSettings = convertSettingsSliceFromGeneric(Category.Licensing, licenseSettings);
 * //  {
 * //    projectDefaultLicensingStatusCheckEnabled: true,
 * //  }
 */
export const convertSettingsSliceFromGeneric = (
  category: Category,
  settings: Partial<GenericIssueSettings>,
): Partial<AnyIssueSettings> => {
  const categorySpecificKeys = settingsCategoryMap[category];
  return Object.fromEntries(
    Object.entries(settings)
      .map(([key, value]) => [categorySpecificKeys[key as GenericIssueSettingsKeys], value]),
  );
};

/**
 * Takes a category + the saved generic settings and returns
 * the entire mapped category settings. Expects a full set of settings,
 * if you just have a partial to convert use convertSettingsSliceFromGeneric instead.
 *
 * @example
 * const licenseSettings = {
 *   isScanningEnabled: true,
 *   policyId: 1,
 *   isCICheckEnabled: true,
 *   filterId: 1,
 * };
 *
 * const genericSettings = convertSettingsFromGeneric(Category.Licensing, licenseSettings);
 * //  {
 * //    projectDefaultLicensingIssueScanningEnabled: true,
 * //    defaultPolicyId: 1,
 * //    projectDefaultLicensingStatusCheckEnabled: true,
 * //    projectDefaultStatusCheckFilterLicensing: 1,
 * //  }
 */
export const convertSettingsFromGeneric = (
  category: Category,
  settings: GenericIssueSettings,
): AnyIssueSettings => {
  const categorySpecificKeys = settingsCategoryMap[category];

  return Object.fromEntries(
    Object.entries(categorySpecificKeys).map(([genericKey, categoryKey]) =>
      [categoryKey, settings[genericKey as GenericIssueSettingsKeys]]),
  ) as AnyIssueSettings;
};

//  Used for the propagation modal, as that endpoint accepts an array of fields to be propagated.
export const convertToFieldsToPropagate = (
  category: Category,
  checkboxState: PropagateIssueCheckboxes,
): GenericIssueSettingsFields[] => {
  const categorySpecificKeys = settingsCategoryMap[category];

  return _.compact(
    Object.entries(checkboxState).map(([key, value]) => {
      //  If the checkbox isn't checked, do not include this field in the array.
      if (!value) {
        return null;
      }
      //  This handles the two checkbox booleans which are represented as numbers in GenericIssueSettings.
      if (key === 'shouldUseSelectedPolicy') {
        return categorySpecificKeys.policyId;
      }
      if (key === 'shouldUseSelectedFilter') {
        return categorySpecificKeys.filterId;
      }
      return categorySpecificKeys[key as keyof GenericIssueSettings] || false;
    }),
  ) as GenericIssueSettingsFields[];
};
